import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import confetti from 'canvas-confetti';
import { getPoolSongs, getPools } from '../api';
import Bracket from '../components/Bracket';

function nextPowerOfTwo(n) {
  let power = 1;
  while (power < n) power *= 2;
  return power;
}

function createSoloBracket(songs) {
  const size = nextPowerOfTwo(songs.length);
  const shuffled = [...songs].sort(() => Math.random() - 0.5);
  while (shuffled.length < size) shuffled.push(null);

  const rounds = Math.log2(size);
  const matches = [];

  for (let i = 0; i < size; i += 2) {
    matches.push({
      id: `match-${i / 2}`,
      round: 0,
      songA: shuffled[i],
      songB: shuffled[i + 1],
      winner: null,
      isBye: shuffled[i] === null || shuffled[i + 1] === null
    });
  }

  // Resolve byes
  matches.forEach(match => {
    if (match.isBye) {
      if (match.songA === null && match.songB !== null) match.winner = 'b';
      else if (match.songB === null && match.songA !== null) match.winner = 'a';
    }
  });

  return { matches, rounds, currentRound: 0 };
}

function SoloMode() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState('genre'); // genre | loading | battle | finished
  const [pools, setPools] = useState([]);
  const [poolsLoading, setPoolsLoading] = useState(true);
  const [selectedPool, setSelectedPool] = useState(null);
  const [songCount, setSongCount] = useState(8);
  const [bracket, setBracket] = useState(null);
  const [currentMatch, setCurrentMatch] = useState(null);
  const [champion, setChampion] = useState(null);
  const [showBracket, setShowBracket] = useState(false);
  const [error, setError] = useState('');
  const matchCounter = useRef(0);

  useEffect(() => {
    getPools()
      .then(data => setPools(data.pools || []))
      .catch(() => setError('Sammlungen konnten nicht geladen werden.'))
      .finally(() => setPoolsLoading(false));
  }, []);

  const startBattle = async (pool) => {
    setSelectedPool(pool);
    setPhase('loading');
    setError('');
    try {
      const data = await getPoolSongs(pool.id, songCount);
      if (!data.tracks || data.tracks.length < 4) {
        setError('Nicht genügend Songs in dieser Sammlung. Importiere mehr Songs oder wähle eine andere.');
        setPhase('genre');
        return;
      }
      const b = createSoloBracket(data.tracks);
      setBracket(b);
      advanceToNext(b);
      setPhase('battle');
    } catch (err) {
      setError('Fehler beim Laden der Songs. Bitte versuche es erneut.');
      setPhase('genre');
    }
  };

  const advanceToNext = (b) => {
    const next = b.matches.find(m => m.round === b.currentRound && m.winner === null);
    if (next) {
      setCurrentMatch(next);
      return;
    }

    // Check if round is complete
    const currentRoundMatches = b.matches.filter(m => m.round === b.currentRound);
    const allDone = currentRoundMatches.every(m => m.winner !== null);

    if (allDone) {
      if (b.currentRound >= b.rounds - 1) {
        // Tournament finished
        const finalMatch = b.matches[b.matches.length - 1];
        const c = finalMatch.winner === 'a' ? finalMatch.songA : finalMatch.songB;
        setChampion(c);
        setPhase('finished');
        confetti({ particleCount: 200, spread: 100, origin: { y: 0.6 } });
        return;
      }

      // Create next round
      const winners = currentRoundMatches.map(m => m.winner === 'a' ? m.songA : m.songB);
      for (let i = 0; i < winners.length; i += 2) {
        matchCounter.current++;
        b.matches.push({
          id: `match-${Date.now()}-${matchCounter.current}`,
          round: b.currentRound + 1,
          songA: winners[i],
          songB: winners[i + 1] || null,
          winner: null,
          isBye: winners[i] === null || (winners[i + 1] || null) === null
        });
      }

      // Resolve byes in new round
      b.matches.filter(m => m.round === b.currentRound + 1 && m.isBye).forEach(match => {
        if (match.songA === null && match.songB !== null) match.winner = 'b';
        else if (match.songB === null && match.songA !== null) match.winner = 'a';
      });

      b.currentRound++;
      setBracket({ ...b });
      advanceToNext(b);
    }
  };

  const handleChoice = (choice) => {
    if (!currentMatch) return;
    currentMatch.winner = choice;
    setBracket({ ...bracket });
    setCurrentMatch(null);

    setTimeout(() => {
      advanceToNext(bracket);
    }, 300);
  };

  // Pool + song-count selection
  if (phase === 'genre') {
    const hasPools = pools.length > 0;
    const allCount = pools.reduce((sum, p) => sum + (p.count || 0), 0);

    return (
      <div className="solo-page">
        <div className="solo-container">
          <h1 className="page-title">
            <span className="back-btn" onClick={() => navigate('/')}>←</span>
            Solo Modus
          </h1>

          {error && <div className="error-message">{error}</div>}

          {poolsLoading ? (
            <p className="solo-subtitle">Sammlungen werden geladen...</p>
          ) : !hasPools ? (
            <div style={{ textAlign: 'center', marginTop: '2rem' }}>
              <p style={{ color: 'var(--text-muted)', marginBottom: '1rem' }}>
                Noch keine Sammlungen vorhanden. Importiere zuerst ein paar Playlists.
              </p>
              <button className="btn btn-primary btn-large" onClick={() => navigate('/import')}>
                📥 Songs importieren
              </button>
            </div>
          ) : (
            <>
              <p className="solo-subtitle">Wähle eine Sammlung:</p>
              <div className="pool-grid">
                {allCount > 0 && (
                  <div
                    className={`pool-card ${selectedPool?.id === 'all' ? 'active' : ''}`}
                    onClick={() => setSelectedPool({ id: 'all', name: 'Alle', count: allCount })}
                  >
                    <div className="pool-card-cover pool-card-cover-all">🎵</div>
                    <div className="pool-card-name">Alle</div>
                    <div className="pool-card-count">{allCount} Songs</div>
                  </div>
                )}
                {pools.map(pool => (
                  <div
                    key={pool.id}
                    className={`pool-card ${selectedPool?.id === pool.id ? 'active' : ''}`}
                    onClick={() => setSelectedPool(pool)}
                  >
                    {pool.coverUrl ? (
                      <img src={pool.coverUrl} alt={pool.name} className="pool-card-cover" />
                    ) : (
                      <div className="pool-card-cover pool-card-cover-all">🎧</div>
                    )}
                    <div className="pool-card-name">{pool.name}</div>
                    <div className="pool-card-count">{pool.count || 0} Songs</div>
                  </div>
                ))}
              </div>

              <div className="song-count-section">
                <label>Anzahl Songs:</label>
                <div className="song-count-options">
                  {[4, 8, 16, 32].map(n => (
                    <button
                      key={n}
                      className={`song-count-btn ${songCount === n ? 'active' : ''}`}
                      onClick={() => setSongCount(n)}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>

              <button
                className="btn btn-primary btn-large"
                disabled={!selectedPool}
                onClick={() => selectedPool && startBattle(selectedPool)}
              >
                {selectedPool ? `🎵 "${selectedPool.name}" starten` : 'Sammlung wählen'}
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  // Loading
  if (phase === 'loading') {
    return (
      <div className="solo-page">
        <div className="loading-page">
          <div className="spinner"></div>
          <p>Songs werden geladen...</p>
        </div>
      </div>
    );
  }

  // Champion
  if (phase === 'finished' && champion) {
    return (
      <div className="battle-page champion-screen">
        <div className="champion-container">
          <h1 className="champion-title">🏆 Dein Gewinner! 🏆</h1>
          <div className="champion-song">
            <img src={champion.albumCover} alt={champion.name} className="champion-cover" />
            <h2>{champion.name}</h2>
            <p>{champion.artist}</p>
            <iframe
              src={`https://open.spotify.com/embed/track/${champion.id}?utm_source=generator&theme=0`}
              width="100%" height="152" frameBorder="0"
              allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
              loading="lazy" title="Winner" style={{ borderRadius: 12, marginTop: 16 }}
            />
          </div>
          <div className="champion-actions">
            <button className="btn btn-primary btn-large" onClick={() => setShowBracket(!showBracket)}>
              {showBracket ? 'Gewinner' : '📊 Turnierbaum'}
            </button>
            <button className="btn btn-secondary btn-large" onClick={() => startBattle(selectedPool)}>
              Nochmal spielen
            </button>
            <button className="btn btn-ghost" onClick={() => navigate('/')}>
              Zurück zum Start
            </button>
          </div>
          {showBracket && bracket && (
            <div className="champion-bracket">
              <Bracket bracket={bracket} />
            </div>
          )}
        </div>
      </div>
    );
  }

  // Battle
  if (phase === 'battle' && currentMatch) {
    const roundNames = ['Achtelfinale', 'Viertelfinale', 'Halbfinale', 'Finale'];
    const roundName = roundNames[bracket.currentRound] || `Runde ${bracket.currentRound + 1}`;

    return (
      <div className="battle-page solo-battle">
        <div className="battle-header">
          <h2 className="round-name">{roundName}</h2>
          <button className="btn btn-ghost btn-small" onClick={() => setShowBracket(!showBracket)}>
            {showBracket ? 'Battle' : '📊 Bracket'}
          </button>
        </div>

        {showBracket && bracket ? (
          <Bracket bracket={bracket} />
        ) : (
          <div className="matchup">
            <div className="battle-song-card" onClick={() => handleChoice('a')}>
              <div className="bsc-iframe-container">
                <iframe
                  src={`https://open.spotify.com/embed/track/${currentMatch.songA?.id}?utm_source=generator&theme=0`}
                  width="100%" height="100%" frameBorder="0"
                  allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                  loading="lazy" title={currentMatch.songA?.name} className="bsc-iframe"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
              <div className="bsc-divider" />
              <button className="bsc-vote-btn">SONG AUSWÄHLEN</button>
            </div>

            <div className="vs-divider">
              <span>VS</span>
            </div>

            <div className="battle-song-card" onClick={() => handleChoice('b')}>
              <div className="bsc-iframe-container">
                <iframe
                  src={`https://open.spotify.com/embed/track/${currentMatch.songB?.id}?utm_source=generator&theme=0`}
                  width="100%" height="100%" frameBorder="0"
                  allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                  loading="lazy" title={currentMatch.songB?.name} className="bsc-iframe"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
              <div className="bsc-divider" />
              <button className="bsc-vote-btn">SONG AUSWÄHLEN</button>
            </div>
          </div>
        )}

        <p className="solo-hint">Tippe auf den Song, der gewinnen soll!</p>
      </div>
    );
  }

  return null;
}

export default SoloMode;
